import React from 'react';
import { getSvgColor } from './colorUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LineChartPoint {
  x: number | string;
  y: number;
}

interface LineChartSeries {
  label: string;
  data: LineChartPoint[];
  color?: string;
  dashed?: boolean;
  showDots?: boolean;
}

interface LineChartProps {
  title?: string;
  series: LineChartSeries[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  horizontalLines?: Array<{
    value: number;
    label: string;
    color?: string;
    dashed?: boolean;
  }>;
  showGrid?: boolean;
  height?: number;
  unit?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Round a number to a "nice" tick value for axis labels. */
function niceNum(range: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

/** Compute evenly-spaced tick values for a given data range. */
function computeTicks(min: number, max: number, tickCount: number): number[] {
  if (min === max) {
    // Degenerate case: flat data
    return [min - 1, min - 0.5, min, min + 0.5, min + 1];
  }

  const range = niceNum(max - min, false);
  const spacing = niceNum(range / (tickCount - 1), true);
  const niceMin = Math.floor(min / spacing) * spacing;
  const niceMax = Math.ceil(max / spacing) * spacing;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + spacing * 0.5; v += spacing) {
    ticks.push(parseFloat(v.toFixed(10)));
  }
  return ticks;
}

/** Format a number for axis/label display. */
function formatValue(v: number, unit?: string): string {
  const formatted = Number.isInteger(v) ? v.toString() : v.toFixed(1);
  return unit ? `${formatted} ${unit}` : formatted;
}

// ---------------------------------------------------------------------------
// Constants — SVG layout
// ---------------------------------------------------------------------------

const SVG_WIDTH = 700;
const SVG_HEIGHT = 400;
const PADDING_LEFT = 70;
const PADDING_RIGHT = 80;
const PADDING_TOP = 20;
const PADDING_BOTTOM = 50;

const CHART_WIDTH = SVG_WIDTH - PADDING_LEFT - PADDING_RIGHT;
const CHART_HEIGHT = SVG_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

const GRID_COLOR = '#E5E7EB';
const AXIS_COLOR = '#9CA3AF';
const OUT_OF_CONTROL_COLOR = '#EF4444';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const LineChart: React.FC<LineChartProps> = ({
  title,
  series,
  xAxisLabel,
  yAxisLabel,
  horizontalLines = [],
  showGrid = true,
  height = 300,
  unit,
}) => {
  if (!series || series.length === 0) {
    return null;
  }

  // -----------------------------------------------------------------------
  // 1. Collect all data points to derive axis ranges
  // -----------------------------------------------------------------------

  const allYValues: number[] = [];
  const allXValues: (number | string)[] = [];

  series.forEach((s) => {
    s.data.forEach((pt) => {
      allYValues.push(pt.y);
      allXValues.push(pt.x);
    });
  });

  // Include horizontal-line values in the y-range so they are always visible
  horizontalLines.forEach((hl) => {
    allYValues.push(hl.value);
  });

  if (allYValues.length === 0) {
    return null;
  }

  const rawYMin = Math.min(...allYValues);
  const rawYMax = Math.max(...allYValues);

  // Add 10% padding above and below the raw range
  const yPad = (rawYMax - rawYMin) * 0.1 || 1;
  const yMin = rawYMin - yPad;
  const yMax = rawYMax + yPad;

  const yTicks = computeTicks(yMin, yMax, 5);
  const yAxisMin = yTicks[0];
  const yAxisMax = yTicks[yTicks.length - 1];

  // -----------------------------------------------------------------------
  // 2. Determine x-axis mode (string labels vs. numeric)
  // -----------------------------------------------------------------------

  const isStringX = typeof allXValues[0] === 'string';

  // Deduplicate and preserve order for string x-values
  let xLabels: string[] = [];
  let xNumericMin = 0;
  let xNumericMax = 1;

  if (isStringX) {
    const seen = new Set<string>();
    series.forEach((s) => {
      s.data.forEach((pt) => {
        const key = String(pt.x);
        if (!seen.has(key)) {
          seen.add(key);
          xLabels.push(key);
        }
      });
    });
  } else {
    const numericXs = allXValues.map(Number);
    xNumericMin = Math.min(...numericXs);
    xNumericMax = Math.max(...numericXs);
    if (xNumericMin === xNumericMax) {
      xNumericMin -= 1;
      xNumericMax += 1;
    }
  }

  // -----------------------------------------------------------------------
  // 3. Coordinate mapping functions
  // -----------------------------------------------------------------------

  /** Map a y data value to SVG y coordinate. */
  const yToSvg = (v: number): number => {
    const ratio = (v - yAxisMin) / (yAxisMax - yAxisMin);
    return PADDING_TOP + CHART_HEIGHT * (1 - ratio);
  };

  /** Map an x data value to SVG x coordinate. */
  const xToSvg = (v: number | string): number => {
    if (isStringX) {
      const idx = xLabels.indexOf(String(v));
      if (xLabels.length <= 1) return PADDING_LEFT + CHART_WIDTH / 2;
      return PADDING_LEFT + (idx / (xLabels.length - 1)) * CHART_WIDTH;
    }
    const ratio = (Number(v) - xNumericMin) / (xNumericMax - xNumericMin);
    return PADDING_LEFT + ratio * CHART_WIDTH;
  };

  // -----------------------------------------------------------------------
  // 4. Determine out-of-control bounds from horizontal lines
  // -----------------------------------------------------------------------

  // Find UCL (upper control limit) and LCL (lower control limit) values by
  // looking for lines whose labels contain those abbreviations.
  const uclLine = horizontalLines.find(
    (hl) => hl.label.toUpperCase().includes('UCL'),
  );
  const lclLine = horizontalLines.find(
    (hl) => hl.label.toUpperCase().includes('LCL'),
  );

  const uclValue = uclLine ? uclLine.value : null;
  const lclValue = lclLine ? lclLine.value : null;

  /** Check whether a point is outside control limits. */
  const isOutOfControl = (y: number): boolean => {
    if (uclValue !== null && y > uclValue) return true;
    if (lclValue !== null && y < lclValue) return true;
    return false;
  };

  // -----------------------------------------------------------------------
  // 5. Build x-axis labels
  // -----------------------------------------------------------------------

  let xAxisTicks: { label: string; svgX: number }[] = [];

  if (isStringX) {
    xAxisTicks = xLabels.map((label) => ({
      label,
      svgX: xToSvg(label),
    }));
  } else {
    const numericTicks = computeTicks(xNumericMin, xNumericMax, 6);
    xAxisTicks = numericTicks.map((v) => ({
      label: formatValue(v),
      svgX: xToSvg(v),
    }));
  }

  // -----------------------------------------------------------------------
  // 6. Render
  // -----------------------------------------------------------------------

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Chart area */}
      <div className="px-4 pt-4 pb-2" style={{ minHeight: height }}>
        <svg
          width="100%"
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={title || 'Line chart'}
        >
          {/* ---- Grid lines ---- */}
          {showGrid &&
            yTicks.map((tick) => {
              const y = yToSvg(tick);
              return (
                <line
                  key={`grid-h-${tick}`}
                  x1={PADDING_LEFT}
                  y1={y}
                  x2={PADDING_LEFT + CHART_WIDTH}
                  y2={y}
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                />
              );
            })}

          {showGrid &&
            xAxisTicks.map((tick, i) => (
              <line
                key={`grid-v-${i}`}
                x1={tick.svgX}
                y1={PADDING_TOP}
                x2={tick.svgX}
                y2={PADDING_TOP + CHART_HEIGHT}
                stroke={GRID_COLOR}
                strokeWidth={1}
              />
            ))}

          {/* ---- Axes ---- */}
          {/* Y axis */}
          <line
            x1={PADDING_LEFT}
            y1={PADDING_TOP}
            x2={PADDING_LEFT}
            y2={PADDING_TOP + CHART_HEIGHT}
            stroke={AXIS_COLOR}
            strokeWidth={1.5}
          />
          {/* X axis */}
          <line
            x1={PADDING_LEFT}
            y1={PADDING_TOP + CHART_HEIGHT}
            x2={PADDING_LEFT + CHART_WIDTH}
            y2={PADDING_TOP + CHART_HEIGHT}
            stroke={AXIS_COLOR}
            strokeWidth={1.5}
          />

          {/* ---- Y-axis tick labels ---- */}
          {yTicks.map((tick) => {
            const y = yToSvg(tick);
            return (
              <g key={`ytick-${tick}`}>
                <line
                  x1={PADDING_LEFT - 5}
                  y1={y}
                  x2={PADDING_LEFT}
                  y2={y}
                  stroke={AXIS_COLOR}
                  strokeWidth={1}
                />
                <text
                  x={PADDING_LEFT - 10}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={11}
                  fill="#6B7280"
                >
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}

          {/* ---- X-axis tick labels ---- */}
          {xAxisTicks.map((tick, i) => (
            <g key={`xtick-${i}`}>
              <line
                x1={tick.svgX}
                y1={PADDING_TOP + CHART_HEIGHT}
                x2={tick.svgX}
                y2={PADDING_TOP + CHART_HEIGHT + 5}
                stroke={AXIS_COLOR}
                strokeWidth={1}
              />
              <text
                x={tick.svgX}
                y={PADDING_TOP + CHART_HEIGHT + 20}
                textAnchor="middle"
                fontSize={11}
                fill="#6B7280"
              >
                {tick.label}
              </text>
            </g>
          ))}

          {/* ---- Axis labels ---- */}
          {yAxisLabel && (
            <text
              x={16}
              y={PADDING_TOP + CHART_HEIGHT / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={12}
              fontWeight={600}
              fill="#374151"
              transform={`rotate(-90, 16, ${PADDING_TOP + CHART_HEIGHT / 2})`}
            >
              {yAxisLabel}
            </text>
          )}

          {xAxisLabel && (
            <text
              x={PADDING_LEFT + CHART_WIDTH / 2}
              y={SVG_HEIGHT - 6}
              textAnchor="middle"
              fontSize={12}
              fontWeight={600}
              fill="#374151"
            >
              {xAxisLabel}
            </text>
          )}

          {/* ---- Horizontal reference lines (UCL / LCL / Mean) ---- */}
          {horizontalLines.map((hl, i) => {
            const y = yToSvg(hl.value);
            const lineColor = getSvgColor(hl.color);
            return (
              <g key={`hline-${i}`}>
                <line
                  x1={PADDING_LEFT}
                  y1={y}
                  x2={PADDING_LEFT + CHART_WIDTH}
                  y2={y}
                  stroke={lineColor}
                  strokeWidth={1.5}
                  strokeDasharray={hl.dashed ? '6 3' : undefined}
                />
                <text
                  x={PADDING_LEFT + CHART_WIDTH + 6}
                  y={y}
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={lineColor}
                >
                  {hl.label}
                  {unit ? ` (${formatValue(hl.value, unit)})` : ` (${formatValue(hl.value)})`}
                </text>
              </g>
            );
          })}

          {/* ---- Data series (polylines + dots) ---- */}
          {series.map((s, sIdx) => {
            const color = getSvgColor(s.color);
            const showDots = s.showDots !== false;

            // Build polyline points string
            const points = s.data
              .map((pt) => `${xToSvg(pt.x)},${yToSvg(pt.y)}`)
              .join(' ');

            return (
              <g key={`series-${sIdx}`}>
                {/* Line */}
                <polyline
                  points={points}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeDasharray={s.dashed ? '8 4' : undefined}
                />

                {/* Dots */}
                {showDots &&
                  s.data.map((pt, pIdx) => {
                    const cx = xToSvg(pt.x);
                    const cy = yToSvg(pt.y);
                    const outOfControl = isOutOfControl(pt.y);

                    return (
                      <circle
                        key={`dot-${sIdx}-${pIdx}`}
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill={outOfControl ? OUT_OF_CONTROL_COLOR : color}
                        stroke="#fff"
                        strokeWidth={1.5}
                      />
                    );
                  })}
              </g>
            );
          })}
        </svg>
      </div>

      {/* ---- Legend ---- */}
      {series.length > 0 && (
        <div className="border-t border-gray-200 px-5 py-3 bg-gray-50">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {series.map((s, sIdx) => {
              const color = getSvgColor(s.color);
              return (
                <div
                  key={`legend-${sIdx}`}
                  className="flex items-center gap-1.5"
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <span className="text-xs text-gray-600">{s.label}</span>
                </div>
              );
            })}

            {/* Also show horizontal-line entries in the legend */}
            {horizontalLines.map((hl, i) => {
              const color = getSvgColor(hl.color);
              return (
                <div
                  key={`legend-hl-${i}`}
                  className="flex items-center gap-1.5"
                >
                  <span
                    className="inline-block w-3 h-0.5 flex-shrink-0"
                    style={{
                      backgroundColor: color,
                      borderTop: hl.dashed ? `2px dashed ${color}` : undefined,
                      background: hl.dashed ? 'transparent' : color,
                    }}
                    aria-hidden="true"
                  />
                  <span className="text-xs text-gray-600">{hl.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LineChart;
